import Button from 'components/button';
import CompactEmptyState from 'components/EmptyState/CompactEmptyState';
import { commercialsOnly } from 'components/companies/companyAssignees';
import { apiErrorText, errorCodeOf } from 'helper/apiErrorText';
import useCrmContext from 'hooks/useCrmContext';
import useText from 'hooks/useText';
import { useCallback, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import crmApi from 'server/crm/crmApi';

const GOALS = ['discovery', 'followup'];
// As the api takes them (settings/calls/update, crmProfile DAILY_GOAL_MAX):
// whole numbers of calls a day, from 0 to 100.
const MAX_DAILY_GOAL = 100;
const isDailyGoal = (value) =>
  /^\d+$/.test(value) && Number(value) <= MAX_DAILY_GOAL;
const draftOf = (goals) =>
  Object.fromEntries(
    goals.map((person) => [
      person.personId,
      Object.fromEntries(GOALS.map((key) => [key, String(person[key])])),
    ]),
  );
const nameOf = (person) => `${person.firstname} ${person.lastname}`.trim();

/**
 * « Objectifs d'appels »: the discoveries and follow-ups a day of each
 * Commercial (510 active), which fill their call list. Read and saved by the
 * api (admin/crm/settings/calls); only these two goals are shown, the api
 * reads no other setting of the day. The api lists everyone who prospects,
 * agency managers included, without their role: the team (admin/crm/team)
 * says who the Commercials are, the agency managers keep their goals unseen.
 */
const AdminCallGoals = () => {
  const { text, language } = useText('adminConsole');
  const { call, ready } = useCrmContext();
  const [saved, setSaved] = useState(null);
  const [draft, setDraft] = useState(null);
  const [failed, setFailed] = useState(false);
  const [saving, setSaving] = useState(false);
  // The ids of the Commercials, whose goals alone are shown.
  const [commercialIds, setCommercialIds] = useState(null);

  const show = useCallback((goals, ids) => {
    const shown = goals.filter((person) => ids.has(person.personId));
    setSaved(shown);
    setDraft(draftOf(shown));
  }, []);
  const load = useCallback(() => {
    setFailed(false);
    Promise.all([call(crmApi.getCallSettings), call(crmApi.getTeam)]).then(
      ([{ goals }, team]) => {
        const ids = new Set(commercialsOnly(team ?? []).map((person) => person.id));
        setCommercialIds(ids);
        show(goals, ids);
      },
      () => setFailed(true),
    );
  }, [call, show]);
  useEffect(() => {
    if (ready) load();
  }, [ready, load]);

  if (failed)
    return (
      <CompactEmptyState
        title={text('callGoalsLoadError')}
        action={{ label: text('goalsRetry'), onClick: load }}
      />
    );
  if (!draft)
    return (
      <div
        className="h-40 animate-pulse rounded-lg bg-gray-100"
        aria-busy="true"
      />
    );

  const isChanged = (person) =>
    GOALS.some((key) => draft[person.personId][key] !== String(person[key]));
  const changed = saved.some(isChanged);
  const problem = saved.every((person) =>
    GOALS.every((key) => isDailyGoal(draft[person.personId][key])),
  )
    ? null
    : text('callGoalsInvalid');

  const save = () => {
    setSaving(true);
    call(crmApi.saveCallSettings, {
      goals: saved.filter(isChanged).map((person) => ({
        personId: person.personId,
        discovery: Number(draft[person.personId].discovery),
        followup: Number(draft[person.personId].followup),
      })),
    }).then(
      // The values shown are the api's, as it kept them.
      ({ goals }) => {
        setSaving(false);
        show(goals, commercialIds);
        toast.success(text('callGoalsSaved'));
      },
      (error) => {
        setSaving(false);
        toast.error(apiErrorText(error, { language, text, group: 'callGoalsErrors' }));
        // A person who no longer prospects: the list is stale, reload it.
        if (errorCodeOf(error) === 'UNKNOWN_PERSON') load();
      },
    );
  };

  const field = (person, key, label) => (
    <input
      type="text"
      inputMode="numeric"
      aria-label={text('callGoalsInputLabel', {
        goal: label,
        name: nameOf(person),
      })}
      value={draft[person.personId][key]}
      onChange={(event) =>
        setDraft((current) => ({
          ...current,
          [person.personId]: {
            ...current[person.personId],
            [key]: event.target.value,
          },
        }))
      }
      className={`h-9 w-20 rounded-md border bg-white px-3 text-right text-sm text-gray-900 focus:outline-none ${isDailyGoal(draft[person.personId][key]) ? 'border-gray-200 focus:border-yellow-500' : 'border-red-500'}`}
    />
  );

  return (
    <section
      aria-labelledby="call-goals-title"
      className="w-full overflow-hidden rounded-lg border border-gray-100 bg-white"
    >
      <div className="flex flex-col gap-0.5 px-4 py-4">
        <h2
          id="call-goals-title"
          className="text-base font-semibold text-gray-900"
        >
          {text('callGoalsTitle')}
        </h2>
        <p className="text-sm text-gray-500">{text('callGoalsText')}</p>
      </div>
      {saved.length === 0 ? (
        <p className="border-t border-gray-100 px-4 py-6 text-center text-sm text-gray-500">
          {text('callGoalsNone')}
        </p>
      ) : (
        <table className="w-full text-sm">
          <thead>
            <tr className="border-y border-gray-100">
              <th className="bg-gray-50 py-3.5 pl-4 pr-2 text-left font-medium uppercase text-gray-700">
                {text('callGoalsPerson')}
              </th>
              <th className="bg-gray-50 px-2 py-3.5 text-right font-medium uppercase text-gray-700">
                {text('callGoalsDiscovery')}
              </th>
              <th className="bg-gray-50 py-3.5 pl-2 pr-4 text-right font-medium uppercase text-gray-700">
                {text('callGoalsFollowup')}
              </th>
            </tr>
          </thead>
          <tbody>
            {saved.map((person) => (
              <tr
                key={person.personId}
                className="border-b border-gray-50 last:border-0"
              >
                <td className="py-3 pl-4 pr-2 text-gray-900">
                  {nameOf(person)}
                </td>
                <td className="px-2 py-3 text-right">
                  {field(person, 'discovery', text('callGoalsDiscovery'))}
                </td>
                <td className="py-3 pl-2 pr-4 text-right">
                  {field(person, 'followup', text('callGoalsFollowup'))}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {changed && (
        <div className="flex items-center justify-end gap-3 border-t border-gray-100 px-4 py-3">
          <span className="mr-auto text-xs text-gray-500">
            {problem ?? text('goalsUnsaved')}
          </span>
          <Button
            color="SECONDARY"
            size="SM"
            onClick={() => setDraft(draftOf(saved))}
          >
            {text('goalsCancel')}
          </Button>
          <Button
            color="PRIMARY"
            size="SM"
            disabled={Boolean(problem) || saving}
            onClick={save}
          >
            {text('goalsSave')}
          </Button>
        </div>
      )}
    </section>
  );
};

export default AdminCallGoals;
